// Department FAQ (Q&A) domain: default items, a synced store, path filtering,
// HTML sanitisation, and admin CRUD. Faithfully reconstructed from the build.

import { getQA, putQA } from "../lib/api";

export interface QAItem {
  id: string;
  path?: string;
  paths?: string[];
  questionAr: string;
  questionEn: string;
  answerHtmlAr: string;
  answerHtmlEn: string;
  order: number;
  enabled: boolean;
}

export const DEFAULT_QA: QAItem[] = [
  { id: "qa-001", path: "all", questionAr: "ما هي ساعات الدوام الرسمية في كادن؟", questionEn: "What are the official working hours at Kaden?", answerHtmlAr: "<p>الدوام الرسمي من <strong>الأحد إلى الخميس</strong>، من <strong>8:00 صباحاً حتى 5:00 مساءً</strong>، مع استراحة غداء لمدة ساعة. الجمعة والسبت إجازة أسبوعية.</p>", answerHtmlEn: "<p>Official working hours are <strong>Sunday to Thursday</strong>, from <strong>8:00 AM to 5:00 PM</strong>, with a one-hour lunch break. Friday and Saturday are weekends.</p>", order: 1, enabled: true },
  { id: "qa-002", path: "all", questionAr: "كم يوم إجازة سنوية أحصل عليها؟", questionEn: "How many annual leave days do I get?", answerHtmlAr: "<p>وفقاً لنظام العمل السعودي:</p><ul><li><strong>21 يوم</strong> سنوياً للموظفين الذين أمضوا أقل من 5 سنوات.</li><li><strong>30 يوم</strong> سنوياً للموظفين الذين أمضوا 5 سنوات فأكثر.</li></ul>", answerHtmlEn: "<p>According to Saudi Labor Law:</p><ul><li><strong>21 days</strong> annually for employees with less than 5 years of service.</li><li><strong>30 days</strong> annually for employees with 5+ years of service.</li></ul>", order: 2, enabled: true },
  { id: "qa-003", path: "finance", questionAr: "ما هي دورة اعتماد الفواتير في قسم المالية؟", questionEn: "What is the invoice approval cycle in Finance?", answerHtmlAr: "<ol><li>استلام الفاتورة من المورّد عبر بريد <em>finance@kaden-sa.com</em>.</li><li>التحقق من المطابقة مع أمر الشراء (PO).</li><li>اعتماد المدير المباشر ثم المدير المالي.</li><li>الصرف خلال <strong>15 يوم عمل</strong> من تاريخ الاعتماد.</li></ol>", answerHtmlEn: "<ol><li>Receive invoice from vendor via <em>finance@kaden-sa.com</em>.</li><li>Verify match with the Purchase Order (PO).</li><li>Direct manager and Finance Manager approval.</li><li>Payment within <strong>15 business days</strong> of approval.</li></ol>", order: 1, enabled: true },
  { id: "qa-004", path: "procurement", questionAr: "كيف أبدأ طلب شراء جديد؟", questionEn: "How do I initiate a new purchase request?", answerHtmlAr: '<p>افتح <strong>نظام المشتريات</strong> ثم اختر "طلب شراء جديد"، واملأ الحقول التالية:</p><ul><li>اسم المورّد</li><li>وصف الأصناف والكميات</li><li>السعر التقديري</li><li>مركز التكلفة</li></ul><p>سيتم تحويل الطلب لاعتماد المدير ثم قسم المشتريات.</p>', answerHtmlEn: '<p>Open the <strong>Procurement System</strong> and select "New Purchase Request", then fill in:</p><ul><li>Vendor name</li><li>Item descriptions and quantities</li><li>Estimated price</li><li>Cost center</li></ul><p>The request will route to your manager and then to Procurement.</p>', order: 1, enabled: true },
  { id: "qa-005", path: "hr-admin", questionAr: "كيف أُحدّث بياناتي الشخصية في النظام؟", questionEn: "How do I update my personal data in the system?", answerHtmlAr: '<p>افتح <strong>نظام الموارد البشرية</strong>، ثم انتقل إلى <em>"البيانات الشخصية"</em>. يمكنك تحديث:</p><ul><li>رقم الجوال والعنوان</li><li>بيانات الحساب البنكي</li><li>المستندات الشخصية (الهوية، الإقامة)</li></ul><p>التغييرات الحساسة تتطلب موافقة الموارد البشرية.</p>', answerHtmlEn: '<p>Open the <strong>HR System</strong>, then go to <em>"Personal Data"</em>. You can update:</p><ul><li>Mobile number and address</li><li>Bank account information</li><li>Personal documents (ID, Iqama)</li></ul><p>Sensitive changes require HR approval.</p>', order: 1, enabled: true },
];

const QA_CHANGED = "kd-qna-changed";
let items: QAItem[] = [...DEFAULT_QA];
let loaded = false;

function emitChange() {
  try {
    window.dispatchEvent(new Event(QA_CHANGED));
  } catch {
    /* ignore */
  }
}

export function subscribeQA(cb: () => void): () => void {
  window.addEventListener(QA_CHANGED, cb);
  return () => window.removeEventListener(QA_CHANGED, cb);
}

export function getQAItems(): QAItem[] {
  return items;
}

/** Which paths an item belongs to (paths[] wins, else [path], else ["all"]). */
export function itemPaths(item: QAItem): string[] {
  return Array.isArray(item.paths) && item.paths.length > 0 ? item.paths : item.path ? [item.path] : ["all"];
}

/** A blank Q&A item for the "add" form. */
export function newQAItem(): Omit<QAItem, "id" | "order"> {
  return { path: "all", paths: ["all"], questionAr: "", questionEn: "", answerHtmlAr: "", answerHtmlEn: "", enabled: true };
}

/** Enabled items for a given career path (plus universal "all"), ordered. */
export function getQAForPath(path: string): QAItem[] {
  return getQAItems()
    .filter((it) => {
      if (!it.enabled) return false;
      const p = itemPaths(it);
      return p.includes("all") || p.includes(path);
    })
    .sort((a, b) => a.order - b.order);
}

/** Load Q&A from the backend; seed defaults if the store is empty. */
export async function loadQA(): Promise<void> {
  const remote = await getQA<QAItem>();
  if (Array.isArray(remote) && remote.length > 0) {
    items = remote;
    loaded = true;
    emitChange();
  } else if (Array.isArray(remote) && remote.length === 0 && !loaded) {
    await putQA(DEFAULT_QA);
    items = [...DEFAULT_QA];
    loaded = true;
    emitChange();
  }
}

export async function saveQA(next: QAItem[]): Promise<{ ok: boolean; error?: string }> {
  items = next;
  emitChange();
  const res = await putQA(next);
  return res && (res as any).ok ? { ok: true } : { ok: false, error: "Failed to sync Q&A to backend" };
}

// --- Admin CRUD ---
export function resetQA() {
  return saveQA([...DEFAULT_QA]);
}
export async function addQA(item: Omit<QAItem, "id" | "order"> & { path?: string; paths?: string[] }): Promise<QAItem> {
  const all = getQAItems();
  const path = Array.isArray(item.paths) && item.paths.length > 0 ? item.paths[0] : item.path || "all";
  const sameGroup = all.filter((it) => itemPaths(it).includes(path));
  const created: QAItem = {
    ...(item as QAItem),
    path,
    id: "qa-" + Date.now().toString(36) + Math.random().toString(36).slice(2, 6),
    order: sameGroup.length > 0 ? Math.max(...sameGroup.map((it) => it.order)) + 1 : 1,
  };
  await saveQA([...all, created]);
  return created;
}
export function updateQA(id: string, patch: Partial<QAItem>) {
  return saveQA(getQAItems().map((it) => (it.id === id ? { ...it, ...patch, id: it.id } : it)));
}
export function deleteQA(id: string) {
  return saveQA(getQAItems().filter((it) => it.id !== id));
}
export async function reorderQA(id: string, dir: "up" | "down") {
  const all = [...getQAItems()];
  const idx = all.findIndex((it) => it.id === id);
  if (idx === -1) return;
  const target = all[idx];
  const group = all.filter((it) => it.path === target.path).sort((a, b) => a.order - b.order);
  const pos = group.findIndex((it) => it.id === id);
  const swapWith = dir === "up" ? pos - 1 : pos + 1;
  if (swapWith < 0 || swapWith >= group.length) return;
  const other = group[swapWith];
  const tmp = target.order;
  target.order = other.order;
  other.order = tmp;
  await saveQA(all);
}
export function exportQA(): string {
  return JSON.stringify(getQAItems(), null, 2);
}
export async function importQA(json: string): Promise<boolean> {
  try {
    const parsed = JSON.parse(json);
    return Array.isArray(parsed) ? (await saveQA(parsed)).ok : false;
  } catch {
    return false;
  }
}

/** Strip dangerous markup from admin-authored answer HTML before rendering. */
export function sanitizeHtml(html: string): string {
  if (!html) return "";
  let out = html.replace(/<\/?(?:script|iframe|style|object|embed|link|meta)[^>]*>/gi, "");
  out = out.replace(/\son\w+="[^"]*"/gi, "");
  out = out.replace(/\son\w+='[^']*'/gi, "");
  out = out.replace(/javascript\s*:/gi, "#");
  return out;
}
